import React from 'react';
import { Draggable } from '@hello-pangea/dnd';
import { Box, Text, Flex, Avatar, AvatarGroup, Tooltip, Badge } from '@chakra-ui/react';
import { Calendar, Clock, CurrencyDollar, Paperclip, WarningCircle } from 'phosphor-react';
import { IKanbanCard, IKanbanCampoValor } from '../../hooks/useKanban';
import { KanbanPriorityBadge } from './KanbanPriorityBadge';
import { SlaBadge } from './SlaBadge';
import { instanteDeChegada } from './chegadaNaColuna';

interface KanbanCardProps {
  card: IKanbanCard;
  index: number;
  /** Recebe o card: uma função por card, criada na coluna, derrubava o memo e o quadro todo redesenhava. */
  // eslint-disable-next-line no-unused-vars
  onCardClick: (card: IKanbanCard) => void;
  // Espelho read-only (quadro de módulo, task #757): desabilita o arraste.
  isReadOnly?: boolean;
  /** Selo "Novo" e "chegou há…" (quadro do evento: o robô move cards de hora em hora). */
  destacarRecentes?: boolean;
  /**
   * Selo "Novo" e texto "chegou há…", calculados na coluna com o relógio do board. Vêm prontos (e não o
   * relógio) para o card só redesenhar quando eles mudam, e não os 156 cards a cada minuto.
   */
  novo?: boolean;
  chegada?: string | null;
}

const formatCampoValor = (campo: IKanbanCampoValor): string => {
  if (campo.valor === null || campo.valor === undefined) return '';
  switch (campo.tipo) {
    case 'moeda':
      return `R$ ${Number(campo.valor).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    case 'numero':
      return Number(campo.valor).toLocaleString('pt-BR');
    case 'checkbox':
      return campo.valor ? 'Sim' : 'Nao';
    case 'data':
      if (typeof campo.valor === 'string') {
        const [y, m, d] = campo.valor.split('-');
        return `${d}/${m}/${y}`;
      }
      return String(campo.valor);
    default:
      return String(campo.valor);
  }
};

const getCampoIcon = (tipo: string) => {
  switch (tipo) {
    case 'moeda':
      return <CurrencyDollar size={11} weight="bold" />;
    case 'data':
      return <Calendar size={11} weight="bold" />;
    default:
      return null;
  }
};

const KanbanCardInner: React.FC<KanbanCardProps> = ({
  card,
  index,
  onCardClick,
  isReadOnly = false,
  destacarRecentes = false,
  novo = false,
  chegada = null,
}) => {
  const instante = destacarRecentes ? instanteDeChegada(card) : null;
  const corDaBordaEsquerda = (arrastando: boolean) => {
    if (card.assinatura_rejeitada) return 'red.400';
    if (novo) return 'blue.400';
    return arrastando ? 'blue.300' : 'gray.100';
  };
  const hasUsuarios = card.usuarios && card.usuarios.length > 0;
  const supervisores = card.supervisores ?? [];
  const hasSupervisores = supervisores.length > 0;
  const servicos = card.servicos || [];
  const visibleCampos = (card.campo_valores || [])
    .filter(cv => cv.valor !== null && cv.valor !== undefined && cv.valor !== '')
    .slice(0, 2);

  return (
    <Draggable draggableId={`card-${card.id}`} index={index} isDragDisabled={isReadOnly}>
      {(provided, snapshot) => (
        <Box
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          bg="white"
          p={3}
          mb={2}
          borderRadius="12px"
          boxShadow={
            snapshot.isDragging ? '0 12px 28px rgba(0,0,0,0.18)' : '0 1px 2px rgba(0,0,0,0.04)'
          }
          border="1px solid"
          borderColor={snapshot.isDragging ? 'blue.300' : 'gray.100'}
          borderLeft={card.assinatura_rejeitada || novo ? '3px solid' : '1px solid'}
          borderLeftColor={corDaBordaEsquerda(snapshot.isDragging)}
          cursor="pointer"
          onClick={() => onCardClick(card)}
          _hover={{
            borderColor: 'gray.200',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          }}
          transition="all 0.15s ease"
        >
          {/* Title + Priority */}
          <Flex justifyContent="space-between" alignItems="flex-start" gap={2} mb={1}>
            <Text
              fontWeight="500"
              fontSize="13px"
              color="gray.800"
              noOfLines={2}
              flex={1}
              lineHeight="1.4"
            >
              {card.titulo}
            </Text>
            {novo && (
              <Badge
                colorScheme="blue"
                variant="solid"
                fontSize="9px"
                px={1.5}
                borderRadius="full"
                textTransform="uppercase"
                flexShrink={0}
              >
                Novo
              </Badge>
            )}
            <KanbanPriorityBadge prioridade={card.prioridade} />
          </Flex>

          {/* Description */}
          {card.descricao && (
            <Text fontSize="12px" color="gray.400" noOfLines={1} mb={1.5}>
              {card.descricao.length > 60
                ? `${card.descricao.substring(0, 60)}...`
                : card.descricao}
            </Text>
          )}

          {/* Semaforo de SLA (#761) — so aparece se o card tem expira_em */}
          {card.sla && (
            <Box mb={1.5}>
              <SlaBadge sla={card.sla} />
            </Box>
          )}

          {/* Servicos */}
          {servicos.length > 0 && (
            <Flex gap={1} flexWrap="wrap" mb={1} mt={1}>
              {servicos.map(s => (
                <Badge
                  key={s.id}
                  fontSize="9px"
                  fontWeight="500"
                  px={1.5}
                  py={0.5}
                  borderRadius="full"
                  bg={s.categoria_cor ? `${s.categoria_cor}18` : 'purple.50'}
                  color={s.categoria_cor || 'purple.600'}
                  textTransform="none"
                  lineHeight="1.3"
                >
                  {s.nome}
                </Badge>
              ))}
            </Flex>
          )}

          {/* Custom field values (max 2) */}
          {visibleCampos.length > 0 && (
            <Flex gap={1.5} flexWrap="wrap" mb={hasUsuarios ? 1.5 : 0} mt={1}>
              {visibleCampos.map(cv => (
                <Badge
                  key={cv.campo_id}
                  variant="subtle"
                  bg="gray.50"
                  color="gray.600"
                  fontSize="10px"
                  fontWeight="500"
                  px={1.5}
                  py={0.5}
                  borderRadius="md"
                  display="flex"
                  alignItems="center"
                  gap={0.5}
                  textTransform="none"
                >
                  {getCampoIcon(cv.tipo)}
                  <Text as="span" color="gray.400" mr={0.5}>
                    {cv.campo_nome}:
                  </Text>
                  {formatCampoValor(cv)}
                </Badge>
              ))}
            </Flex>
          )}

          {/* Footer: Users + Anexos + Rejeição */}
          {(hasUsuarios ||
            hasSupervisores ||
            card.anexos_count > 0 ||
            card.assinatura_rejeitada ||
            chegada) && (
            <Flex
              alignItems="center"
              justifyContent="space-between"
              mt={2}
              pt={2}
              borderTop="1px solid"
              borderColor="gray.50"
            >
              {hasUsuarios || hasSupervisores ? (
                <Flex alignItems="center" gap={2}>
                  {hasUsuarios && (
                    <Flex alignItems="center">
                      <AvatarGroup size="xs" max={3} spacing="-5px">
                        {card.usuarios.map(usuario => (
                          <Tooltip
                            key={usuario.id}
                            label={usuario.nome || 'Usuario'}
                            placement="top"
                            hasArrow
                            bg="gray.700"
                            color="white"
                            fontSize="11px"
                            borderRadius="md"
                            px={2}
                            py={1}
                          >
                            <Avatar name={usuario.nome || 'U'} size="xs" />
                          </Tooltip>
                        ))}
                      </AvatarGroup>
                      {card.usuarios.length === 1 && !hasSupervisores && (
                        <Text fontSize="11px" color="gray.400" ml={1.5} noOfLines={1}>
                          {card.usuarios[0].nome || 'Sem nome'}
                        </Text>
                      )}
                    </Flex>
                  )}
                  {/* Supervisores — borda roxa p/ distinguir do responsável */}
                  {hasSupervisores && (
                    <AvatarGroup size="xs" max={3} spacing="-5px">
                      {supervisores.map(sup => (
                        <Tooltip
                          key={sup.id}
                          label={`${sup.nome || 'Usuario'} (Supervisor)`}
                          placement="top"
                          hasArrow
                          bg="purple.700"
                          color="white"
                          fontSize="11px"
                          borderRadius="md"
                          px={2}
                          py={1}
                        >
                          <Avatar
                            name={sup.nome || 'S'}
                            size="xs"
                            border="2px solid"
                            borderColor="purple.400"
                          />
                        </Tooltip>
                      ))}
                    </AvatarGroup>
                  )}
                </Flex>
              ) : (
                <Box />
              )}
              <Flex alignItems="center" gap={2.5}>
                {chegada && (
                  <Tooltip
                    label={
                      instante
                        ? `Entrou na coluna em ${new Date(instante).toLocaleString('pt-BR', {
                            dateStyle: 'short',
                            timeStyle: 'short',
                          })}`
                        : chegada
                    }
                    placement="top"
                    hasArrow
                    bg="gray.700"
                    color="white"
                    fontSize="11px"
                    borderRadius="md"
                    px={2}
                    py={1}
                  >
                    <Flex alignItems="center" gap={1} color={novo ? 'blue.500' : 'gray.400'}>
                      <Clock size={12} />
                      <Text fontSize="10px" fontWeight={novo ? '600' : '500'} whiteSpace="nowrap">
                        {chegada}
                      </Text>
                    </Flex>
                  </Tooltip>
                )}
                {card.assinatura_rejeitada && (
                  <Tooltip
                    label="Assinatura rejeitada"
                    placement="top"
                    hasArrow
                    bg="gray.700"
                    color="white"
                    fontSize="11px"
                    borderRadius="md"
                    px={2}
                    py={1}
                  >
                    <Flex alignItems="center" gap={1} color="red.400">
                      <WarningCircle size={13} weight="fill" />
                      <Text fontSize="10px" fontWeight="500">
                        Rejeitada
                      </Text>
                    </Flex>
                  </Tooltip>
                )}
                {card.anexos_count > 0 && (
                  <Tooltip
                    label={`${card.anexos_count} anexo${card.anexos_count > 1 ? 's' : ''}`}
                    placement="top"
                    hasArrow
                    bg="gray.700"
                    color="white"
                    fontSize="11px"
                    borderRadius="md"
                    px={2}
                    py={1}
                  >
                    <Flex alignItems="center" gap={1} color="gray.400">
                      <Paperclip size={12} />
                      <Text fontSize="11px" fontWeight="500">
                        {card.anexos_count}
                      </Text>
                    </Flex>
                  </Tooltip>
                )}
              </Flex>
            </Flex>
          )}
        </Box>
      )}
    </Draggable>
  );
};

export const KanbanCard = React.memo(KanbanCardInner);
